import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { NotificationBell } from './NotificationBell';
import { LanguageSwitcher } from './LanguageSwitcher';
import { activeEntry, activeTabs, type AppNavigation, type MobileNavItem, type NavLink } from '../navigation/appNavigation';
import { useTodoCount } from '../navigation/todoCount';
import { SUPPORT_CONTACT_PATH } from '../constants/support';
import { supportContextFromLocation } from '../utils/supportContext';

interface AppShellProps {
  navigation: AppNavigation;
  userName: string;
  userEmail: string;
  /** Content above the page (environment bar, overlays). */
  before?: React.ReactNode;
  children: React.ReactNode;
  onLogout: () => void;
}

/**
 * The flat side menu and page frame, proposal 1 (ClickUp 869fdak39). The menu
 * is docked on the left from 992 px and slides in from the left below that.
 * A page's sibling pages show as tabs right under its heading. On phones the
 * field roles get a bar along the bottom edge instead of the menu button; its
 * "Mer" opens the same menu.
 */
export function AppShell({ navigation, userName, userEmail, before, children, onLogout }: AppShellProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const ids = useId();
  const [accountOpen, setAccountOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const accountButton = useRef<HTMLButtonElement>(null);
  const accountPanel = useRef<HTMLDivElement>(null);
  const menuButton = useRef<HTMLButtonElement>(null);
  const sidebar = useRef<HTMLElement>(null);
  const mainRef = useRef<HTMLElement>(null);

  const current = activeEntry(navigation, location.pathname);
  const tabs = activeTabs(navigation, location.pathname);
  const todoCount = useTodoCount();

  useEffect(() => {
    setAccountOpen(false);
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.classList.add('app-nav-v2');
    return () => document.body.classList.remove('app-nav-v2');
  }, []);

  // The account menu closes on Escape (back on its button) and on a click outside.
  useEffect(() => {
    if (!accountOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setAccountOpen(false);
      accountButton.current?.focus();
    };
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (accountPanel.current?.contains(target) || accountButton.current?.contains(target)) return;
      setAccountOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [accountOpen]);

  // A link with a #hash (e.g. "Att göra" to /home#att-gora) scrolls to that
  // part once the page has drawn it, which may be after its data has loaded.
  // Content above it can still grow or shrink afterwards (the company home's
  // guide folds away, data arrives), so the part is kept in place until the
  // page has settled or the viewer scrolls themselves.
  useEffect(() => {
    const id = decodeURIComponent(location.hash.slice(1));
    const main = mainRef.current;
    if (!id || !main) return;
    let done = false;
    let settle: number | undefined;
    const reveal = () => {
      const target = document.getElementById(id);
      if (!target) return false;
      target.scrollIntoView({ block: 'start' });
      return true;
    };
    const finish = () => {
      done = true;
      observer.disconnect();
      resize?.disconnect();
      window.clearTimeout(settle);
      ['wheel', 'touchstart', 'keydown'].forEach(type => window.removeEventListener(type, finish));
    };
    // Stops once nothing has moved for a second, and after 10 s at the latest.
    const keep = () => {
      if (done || !reveal()) return;
      window.clearTimeout(settle);
      settle = window.setTimeout(finish, 1000);
    };
    const observer = new MutationObserver(keep);
    observer.observe(main, { childList: true, subtree: true });
    const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(keep);
    resize?.observe(main);
    ['wheel', 'touchstart', 'keydown'].forEach(type => window.addEventListener(type, finish, { passive: true }));
    keep();
    const stop = window.setTimeout(finish, 10000);
    return () => {
      finish();
      window.clearTimeout(stop);
    };
  }, [location.pathname, location.hash, location.key]);

  // The slide-in menu (below 992 px): focus its first link, Escape closes it.
  useEffect(() => {
    if (!menuOpen) return;
    sidebar.current?.querySelector<HTMLElement>('a[href]')?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setMenuOpen(false);
      menuButton.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  // The tabs go right under the page's own heading (.page-header). Pages draw
  // their heading themselves, often after loading, so watch for it; a page
  // without one gets the tabs at the top. The heading also names the page in
  // the browser tab: "Heading · Menu item · Etappio".
  const [tabSlot, setTabSlot] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    const main = mainRef.current;
    if (!main) return;
    const slot = document.createElement('div');
    slot.className = 'app2-tabs-slot';
    const update = () => {
      const heading = main.querySelector('.page-header');
      const headingText = heading?.querySelector('h1, h2, h3, h4, h5')?.textContent?.trim();
      const parts = [headingText, current?.label].filter((part, index, all): part is string => Boolean(part) && all.indexOf(part) === index);
      document.title = [...parts, 'Etappio'].join(' · ');
      if (tabs.length === 0) return;
      if (heading && slot.previousElementSibling !== heading) heading.after(slot);
      setTabSlot(heading ? slot : null);
    };
    update();
    const observer = new MutationObserver(update);
    observer.observe(main, { childList: true, subtree: true, characterData: true });
    return () => {
      observer.disconnect();
      slot.remove();
      setTabSlot(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, tabs.length, current?.label]);

  const menuLink = (item: NavLink, isCurrent: boolean, className = 'app2-side-link') => (
    <Link
      key={item.to + item.label}
      to={item.disabled ? '#' : item.to}
      state={item.to === SUPPORT_CONTACT_PATH ? { supportContext: supportContextFromLocation(location) } : undefined}
      className={className}
      aria-current={isCurrent ? 'page' : undefined}
      aria-disabled={item.disabled || undefined}
      onClick={event => { if (item.disabled) event.preventDefault(); }}
    >
      <i className={`feather-${item.icon}`} aria-hidden="true"></i>
      <span>{item.label}</span>
      {item.attention && <span className="nav-attention-dot" data-testid="nav-attention-dot" aria-hidden="true"></span>}
      {item.attention && <span className="visually-hidden">{t('nav.companyProfileIncomplete')}</span>}
      {!!item.badge && <span className="nav-count-badge" data-testid="nav-count-badge" aria-hidden="true">{item.badge > 9 ? '9+' : item.badge}</span>}
      {!!item.badge && <span className="visually-hidden">{t('appNav.unreadMessages', { count: item.badge })}</span>}
    </Link>
  );

  const tabNav = (
    <nav className="app2-tabs" aria-label={t('appNav.subNav')} data-testid="app-nav-tabs">
      <ul>
        {tabs.map(tab => (
          <li key={tab.to}>
            <Link
              to={tab.disabled ? '#' : tab.to}
              className="app2-tab"
              aria-current={location.pathname === tab.to ? 'page' : undefined}
              aria-disabled={tab.disabled || undefined}
              onClick={event => { if (tab.disabled) event.preventDefault(); }}
            >
              {tab.label}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );

  const onHome = location.pathname === navigation.home;
  // What sits behind "Mer" on phones (the menu, minus what the bottom bar
  // already shows): its counts and attention dots show on "Mer" (ClickUp 869fmfrmm).
  const moreEntries = navigation.sections.flatMap(section => section.entries)
    .filter(item => !item.hidden && !item.inBottomNav);
  const menuBadge = moreEntries.reduce((sum, item) => sum + (item.badge ?? 0), 0);
  const menuAttention = [...moreEntries, ...navigation.bottom].some(item => item.attention);
  const bottomItem = (item: MobileNavItem) => {
    if (item.kind === 'more') {
      return (
        <button
          type="button"
          className="app2-bottom-link"
          aria-expanded={menuOpen}
          aria-controls={`${ids}-sidebar`}
          onClick={() => setMenuOpen(open => !open)}
          data-testid="app-nav-more"
        >
          <span className="app2-bottom-icon">
            <i className={`feather-${menuOpen ? 'x' : item.icon}`} aria-hidden="true"></i>
            {menuBadge > 0 && !menuOpen && <span className="app2-bottom-badge" aria-hidden="true">{menuBadge > 9 ? '9+' : menuBadge}</span>}
            {!menuBadge && menuAttention && !menuOpen && <span className="app2-bottom-dot" data-testid="app-nav-more-attention" aria-hidden="true"></span>}
          </span>
          <span className="app2-bottom-label">{menuOpen ? t('appNav.close') : item.label}</span>
          {menuBadge > 0 && !menuOpen && <span className="visually-hidden">{t('appNav.unreadMessages', { count: menuBadge })}</span>}
          {menuAttention && !menuOpen && <span className="visually-hidden">{t('appNav.moreAttention')}</span>}
        </button>
      );
    }
    const isCurrent = !menuOpen && (item.kind === 'todo'
      ? onHome && location.hash === `#${item.to.split('#')[1]}`
      : item.id === 'home' ? onHome && !location.hash : current?.id === item.id);
    const badge = item.kind === 'todo' ? todoCount ?? 0 : item.badge ?? 0;
    // A counted page (Meddelanden) says its count in its name: "Meddelanden, 3 olästa".
    const ariaLabel = item.kind === 'link' && badge > 0 ? t('appNav.unreadLabel', { label: item.label, count: badge }) : undefined;
    return (
      <Link to={item.to} className="app2-bottom-link" aria-current={isCurrent ? 'page' : undefined} aria-label={ariaLabel} data-testid={item.kind === 'link' ? `app-nav-bottom-${item.id}` : undefined}>
        <span className="app2-bottom-icon">
          <i className={`feather-${item.icon}`} aria-hidden="true"></i>
          {badge > 0 && <span className="app2-bottom-badge" aria-hidden="true">{badge > 9 ? '9+' : badge}</span>}
        </span>
        <span className="app2-bottom-label">{item.label}</span>
        {badge > 0 && item.kind === 'todo' && <span className="visually-hidden">{t('home.todoNow.count', { count: badge })}</span>}
      </Link>
    );
  };

  return (
    <div className={`app2-shell${menuOpen ? ' is-menu-open' : ''}${navigation.mobile ? ' has-bottom-nav' : ''}`}>
      <a href="#app-main" className="app2-skip">{t('appNav.skip')}</a>
      {before}
      <header className="app2-header">
        <div className="app2-header-start">
          <button
            type="button"
            ref={menuButton}
            className="app2-menu-button"
            aria-label={t('appNav.openMenu')}
            aria-expanded={menuOpen}
            aria-controls={`${ids}-sidebar`}
            onClick={() => setMenuOpen(open => !open)}
          >
            <i className="feather-menu" aria-hidden="true"></i>
          </button>
          <Link to={navigation.home} className="app2-logo">
            <img src="/assets/images/logo-full.png" alt={t('appNav.logoHome')} />
          </Link>
        </div>
        <div className="app2-header-end">
          <NotificationBell notificationsPath={navigation.notificationsPath} />
          <div className="app2-account">
            <button
              type="button"
              ref={accountButton}
              className="app2-avatar-button"
              aria-expanded={accountOpen}
              aria-controls={`${ids}-account`}
              aria-label={t('appNav.account', { name: userName })}
              onClick={() => setAccountOpen(open => !open)}
            >
              <span className="app2-avatar" aria-hidden="true">{userName.charAt(0).toUpperCase() || 'U'}</span>
              <span className="app2-avatar-name">{userName}</span>
              <i className="feather-chevron-down" aria-hidden="true"></i>
            </button>
            <div id={`${ids}-account`} ref={accountPanel} className="app2-panel" hidden={!accountOpen}>
              <div className="app2-panel-head">
                <strong>{userName}</strong>
                <span>{userEmail}</span>
              </div>
              {navigation.account.map(item => menuLink(item, location.pathname === item.to, 'app2-panel-link'))}
              <div className="app2-panel-row"><LanguageSwitcher /></div>
              <button type="button" className="app2-panel-link is-danger" onClick={onLogout}>
                <i className="feather-log-out" aria-hidden="true"></i>
                <span>{t('appNav.logout')}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {menuOpen && (
        <button type="button" className="app2-backdrop" aria-label={t('appNav.closeMenu')} onClick={() => setMenuOpen(false)} />
      )}
      <nav id={`${ids}-sidebar`} ref={sidebar} className="app2-sidebar" aria-label={t('appNav.mainNav')} data-testid="app-nav">
        {navigation.primary && (
          <Link to={navigation.primary.to} className="app2-primary" data-testid="app-nav-primary">
            <i className={`feather-${navigation.primary.icon}`} aria-hidden="true"></i>
            <span>{navigation.primary.label}</span>
          </Link>
        )}
        {navigation.sections.map((section, index) => (
          <div key={section.heading ?? index} className="app2-side-section">
            {section.heading && <p className="app2-side-heading">{section.heading}</p>}
            <ul>
              {section.entries.filter(item => !item.hidden).map(item => (
                <li key={item.id} className={item.inBottomNav ? 'is-in-bottom-nav' : undefined}>{menuLink(item, current?.id === item.id)}</li>
              ))}
            </ul>
          </div>
        ))}
        <ul className="app2-side-bottom">
          {navigation.bottom.map(item => <li key={item.to + item.label}>{menuLink(item, false)}</li>)}
        </ul>
      </nav>

      <main id="app-main" className="app2-main" tabIndex={-1} ref={mainRef}>
        {tabs.length > 0 && !tabSlot && tabNav}
        {tabSlot && createPortal(tabNav, tabSlot)}
        {children}
      </main>

      {navigation.mobile && (
        <nav className="app2-bottom-nav" aria-label={t('appNav.mobileNav')} data-testid="app-nav-bottom">
          <ul>
            {navigation.mobile.map(item => (
              <li key={item.kind === 'link' ? item.id : item.kind}>{bottomItem(item)}</li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
}
